/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import toast from "react-hot-toast";

import { ROUTES } from "Router";

import { FetchError, FetchSuccess, fetchV2, fetchV3 } from "lib";

export const getAllPrimaryEventsPagination = async (offset) => {
  try {
    const res = await fetchV3("events?offset=" + offset);
    return new FetchSuccess(res);
  } catch (res) {
    throw new FetchError(res);
  }
};

export const getAllPrimaryEvents = async () => {
  try {
    const res = await fetchV3("all-events", null, {
      shouldSearchForPage: false,
      page: "events",
    });

    return new FetchSuccess(res);
  } catch (res) {
    throw new FetchError(res);
  }
};

export const searchEvents = async (search) => {
  try {
    const res = await fetchV3(`search-events?search=${search?.toLowerCase()}`);
    return new FetchSuccess(res);
  } catch (res) {
    throw new FetchError(res, "Failed to search events");
  }
};

export const getPrimaryEvent = async (id) => {
  try {
    const res = await fetchV3(`events/${id}`, null, {
      page: ROUTES.EVENTS,
    });

    return new FetchSuccess(res);
  } catch (res) {
    throw new FetchError(res);
  }
};

export const getPresetsEvent = async () => {
  try {
    const res = await fetchV3("event-presets");
    return new FetchSuccess(res);
  } catch (res) {
    throw new FetchError(res);
  }
};

export const createPrimaryEvent = async (body) => {
  try {
    const res = await fetchV3("events", {
      method: "POST",
      body: JSON.stringify(body),
    });

    return new FetchSuccess(res, "Event created successfully");
  } catch (res) {
    throw new FetchError(res, "Error creating event");
  }
};

export const updatePrimaryEvent = async (id, body) => {
  try {
    const res = await fetchV3(`events/${id}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    });

    return new FetchSuccess(res, "Event updated successfully");
  } catch (res) {
    throw new FetchError(res, "Error updating event");
  }
};

export const deletePrimaryEvent = async (id) => {
  try {
    const res = await fetchV3(`events/${id}`, {
      method: "DELETE",
    });

    return new FetchSuccess(res, "Event deleted successfully");
  } catch (res) {
    throw new FetchError(res, "Error deleting event");
  }
};

export const cancelPrimaryEvent = async (id) => {
  try {
    const res = await fetchV3(
      `cancel-event/${id}`,
      {
        method: "PATCH",
      },
      {
        page: ROUTES.EVENTS,
      },
    );

    return new FetchSuccess(res, "Event canceled successfully");
  } catch (res) {
    throw new FetchError(res, "Error canceling event");
  }
};

export const reactivatePrimaryEvent = async (id) => {
  try {
    const res = await fetchV3(
      `reactivate-event/${id}`,
      {
        method: "PATCH",
      },
      {
        page: ROUTES.EVENTS,
      },
    );

    return new FetchSuccess(res, "Event reactivated successfully");
  } catch (res) {
    throw new FetchError(res, "Error reactivating  event");
  }
};

export const setupEventLater = async (workspaceId) => {
  const loadingToast = toast.loading("Setting up event...");
  try {
    const res = await fetchV3(
      "events/events",
      {
        method: "POST",
        body: JSON.stringify({
          event_creation_type: "setup_later",
          type: "set_later",
          workspace_id: workspaceId,
        }),
      },
      {
        page: "/",
      },
    );

    toast.dismiss(loadingToast);
    return new FetchSuccess(res, "Event setup later successfully");
  } catch (res) {
    toast.dismiss(loadingToast);
    throw new FetchError(res, "Error setting up event later");
  }
};
// export const setupEventLater = async () => {
//   const loadingToast = toast.loading("Setting up event...");
//   const res = await fetchV3("events", {
//     method: "POST",
//     body: JSON.stringify({
//       event_creation_type: "setup_later",
//       type: "set_later",
//     }),
//   });
//   if (res.ok === false) {
//     toast.dismiss(loadingToast);
//     toast.error("Error setting up event later");
//     return "FAILED";
//   }
//   const data = await res.json();
//   toast.dismiss(loadingToast);
//   toast.success("Event setup later successfully");
//   return data;
// };

export const getPrimaryEventUsers = async (id, offset) => {
  try {
    const res = await fetchV3(`users/${id}?offset=${offset}`);
    return new FetchSuccess(res);
  } catch (res) {
    throw new FetchError(res);
  }
};

export const getDefaultCountryCode = async (id) => {
  try {
    const res = await fetchV3(`events/${id}/default-country-code`, null, {
      page: "/",
      eventTarget: "url",
    });

    return new FetchSuccess(res);
  } catch (res) {
    throw new FetchError(res, "Failed to get country code");
  }
};

export const getEventRecentLocations = async () => {
  try {
    const res = await fetchV3(`recent-locations`, null, {
      eventTarget: "url",
    });

    return new FetchSuccess(res);
  } catch (res) {
    throw new FetchError(
      res,
      "An error happened while fetching recent Locations",
    );
  }
};

export const getPrimaryEventTimeZone = async (eventId) => {
  try {
    const res = await fetchV3(
      `events/supported-time-zones?event_id=${eventId}`,
      null,
      {
        hasSubpath: false,
        eventTarget: "url",
      },
    );

    return new FetchSuccess(res);
  } catch (res) {
    throw new FetchError(res, "Failed to fetch timezones");
  }
};

export const getFormTypes = async () => {
  try {
    const res = await fetchV2(`get-application-forms`, null, {
      page: "users",
    });

    return new FetchSuccess(res);
  } catch (res) {
    throw new FetchError(res, "Failed to fetch users");
  }
};
